"use client";

import { useEffect, useState } from "react";
import {
  AUTO_RETRY_DELAY_MS,
  decideErrorRecovery,
  type RecoveryStorage,
} from "@/lib/runtime/errorRecovery";

const FALLBACK_SCREEN_DELAY_MS = 8_000;

function sessionStorageOrNull(): RecoveryStorage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Recuperação automática das telas de erro (lib/runtime/errorRecovery.ts):
 * um recarregamento completo para erro de carregamento de código/rede, ou
 * uma nova tentativa (retry/reset) depois de um pequeno atraso. Devolve
 * `recovering` = true enquanto a recuperação automática está em andamento;
 * false = mostrar a tela de erro normal.
 */
export function useErrorRecovery(error: unknown, recover: () => void, logTag: string): boolean {
  const [recovering, setRecovering] = useState(true);

  useEffect(() => {
    const digest = error && typeof error === "object" && "digest" in error ? (error as { digest?: unknown }).digest : undefined;
    const message = error instanceof Error ? error.message : undefined;
    const name = error instanceof Error ? error.name : undefined;
    let action: ReturnType<typeof decideErrorRecovery> = "show";
    try {
      action = decideErrorRecovery({ error, path: window.location.pathname, now: Date.now(), storage: sessionStorageOrNull() });
    } catch {
      action = "show";
    }
    console.error(logTag, { digest, name, message, recovery: action });

    if (action === "reload") {
      try {
        window.location.reload();
        return;
      } catch {
        action = "retry";
      }
    }
    if (action === "retry") {
      let fallbackTimer: number | undefined;
      const timer = window.setTimeout(() => {
        // Se a nova tentativa não resolver (boundary continua montado),
        // a tela de erro normal aparece.
        fallbackTimer = window.setTimeout(() => setRecovering(false), FALLBACK_SCREEN_DELAY_MS);
        try {
          recover();
        } catch {
          setRecovering(false);
        }
      }, AUTO_RETRY_DELAY_MS);
      return () => {
        window.clearTimeout(timer);
        if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      };
    }
    const showTimer = window.setTimeout(() => setRecovering(false), 0);
    return () => window.clearTimeout(showTimer);
    // `recover` muda a cada render do boundary; só o erro dispara a decisão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, logTag]);

  return recovering;
}
